import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import NfcRoundedIcon from '@mui/icons-material/NfcRounded'
import { QRCodeCanvas } from 'qrcode.react'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import api from '../../services/api'

export default function MerchantReceiveQr() {
    const { user } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const merchantName = user?.business_name || user?.name || 'MojoPayr Business'
    const merchantId = user?.id || 'merchant'
    const country = user?.business_country || user?.businessCountry || user?.country || 'US'
    const [amount, setAmount] = useState('')
    const [description, setDescription] = useState('')
    const [paymentRequest, setPaymentRequest] = useState(null)
    const [recentRequests, setRecentRequests] = useState([])
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState('')
    const parsedAmount = Number(amount || 0)
    const countryCode = String(country || 'US').toUpperCase()
    const currency = useMemo(() => {
        return user?.default_currency || user?.business_currency || ({
            US: 'USD',
            USA: 'USD',
            CA: 'CAD',
            GB: 'GBP',
            UK: 'GBP',
            AU: 'AUD',
            FR: 'EUR',
            EU: 'EUR',
            SL: 'SLE',
            LR: 'LRD',
            GN: 'GNF',
            GM: 'GMD',
        }[countryCode] || 'USD')
    }, [countryCode, user])
    const qrValue = paymentRequest?.reference
        ? JSON.stringify({ type: 'mojopayr_pos_payment', reference: paymentRequest.reference })
        : ''

    useEffect(() => {
        let active = true
        api.get('/merchant/pos-sales')
            .then((response) => {
                if (!active) return
                setRecentRequests(Array.isArray(response.data?.data) ? response.data.data : [])
            })
            .catch(() => {
                if (active) setRecentRequests([])
            })
        return () => {
            active = false
        }
    }, [])

    const handleCreatePayment = async () => {
        if (!(parsedAmount > 0)) {
            setError(isFr ? 'Entrez un montant valide.' : 'Enter a valid amount.')
            return
        }

        setIsSaving(true)
        try {
            const response = await api.post('/merchant/pos-sales', {
                amount: parsedAmount,
                description: description.trim(),
                qr_payload: '',
            })
            const sale = response.data?.sale
            setPaymentRequest(sale)
            setRecentRequests((current) => [sale, ...current.filter((item) => item.reference !== sale?.reference)].slice(0, 10))
            setError('')
        } catch (err) {
            setError(err?.response?.data?.message || (isFr ? 'Impossible de creer le paiement QR/NFC.' : 'Could not create QR/NFC payment.'))
        } finally {
            setIsSaving(false)
        }
    }

    const handleCopyPayload = async (value = qrValue) => {
        try {
            await navigator.clipboard.writeText(value)
        } catch {
            // no-op fallback for older browsers
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Recevoir paiement' : 'Receive Payment'}
            title={isFr ? 'Recevoir paiement' : 'Receive Payment'}
            subtitle={
                isFr
                    ? 'Affichez un QR marchand ou copiez le payload NFC pour que le client ouvre le paiement en un geste.'
                    : 'Show a merchant QR or copy the NFC payload so the customer can open payment in one step.'
            }
            chips={[
                isFr ? 'QR pret' : 'QR ready',
                isFr ? 'NFC pret' : 'NFC ready',
            ]}
            left={
                <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {paymentRequest && (
                        <Alert severity="success">
                            {isFr
                                ? `Paiement cree. Reference ${paymentRequest.reference}.`
                                : `Payment created. Reference ${paymentRequest.reference}.`}
                        </Alert>
                    )}
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: 5,
                            background: 'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                        }}
                    >
                        <Typography sx={{ color: '#FFFFFF', fontWeight: 900, fontSize: 30 }}>
                            {merchantName}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.6 }}>
                            {merchantId} • {country}
                        </Typography>
                        <Stack direction="row" spacing={1.2} sx={{ mt: 2.2 }}>
                            <Box sx={{ ...shellStyles.softPanel, px: 2, py: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{isFr ? 'QR securise' : 'Secure QR'}</Typography>
                            </Box>
                            <Box sx={{ ...shellStyles.softPanel, px: 2, py: 1 }}>
                                <Typography sx={{ fontWeight: 800 }}>{isFr ? 'NFC reference' : 'NFC reference'}</Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Stack spacing={1.6}>
                            <TextField
                                label={isFr ? 'Montant' : 'Amount'}
                                value={amount}
                                onChange={(event) => {
                                    setAmount(event.target.value)
                                    setPaymentRequest(null)
                                }}
                                placeholder={`${currency} 0.00`}
                            />
                            <TextField
                                label={isFr ? 'Description facultative' : 'Description (optional)'}
                                value={description}
                                onChange={(event) => {
                                    setDescription(event.target.value)
                                    setPaymentRequest(null)
                                }}
                                placeholder={isFr ? 'Ex: comptoir, table 4' : 'Ex: counter, table 4'}
                            />
                            <Button
                                variant="contained"
                                disabled={isSaving || !(parsedAmount > 0)}
                                onClick={handleCreatePayment}
                                sx={{
                                    bgcolor: '#FF8A00',
                                    color: '#0B0620',
                                    borderRadius: 999,
                                    py: 1.35,
                                    fontWeight: 900,
                                    textTransform: 'none',
                                    '&:hover': { bgcolor: '#FF9F2A' },
                                }}
                            >
                                {isSaving
                                    ? (isFr ? 'Creation...' : 'Creating...')
                                    : (isFr ? 'Creer QR/NFC' : 'Create QR/NFC Request')}
                            </Button>
                        </Stack>
                    </Box>

                    <Box sx={{ ...shellStyles.panel, p: 3 }}>
                        <Stack spacing={2} alignItems="center">
                            <Box
                                sx={{
                                    width: 280,
                                    maxWidth: '100%',
                                    aspectRatio: '1 / 1',
                                    borderRadius: 5,
                                    bgcolor: '#FFFFFF',
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: '#0B0620',
                                }}
                            >
                                {qrValue ? (
                                    <QRCodeCanvas value={qrValue} size={240} includeMargin />
                                ) : (
                                    <QrCode2RoundedIcon sx={{ fontSize: 150 }} />
                                )}
                            </Box>
                            {paymentRequest?.reference && (
                                <Typography sx={{ color: '#FF8A00', fontWeight: 900, textAlign: 'center' }}>
                                    {paymentRequest.reference}
                                </Typography>
                            )}
                            <Typography sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 500 }}>
                                {isFr
                                    ? 'Le QR et NFC contiennent seulement la reference. Le serveur fournit le montant, la devise et le statut.'
                                    : 'QR and NFC carry only the reference. The server provides amount, currency, and status.'}
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1.2 }}>
                            {isFr ? 'Tag NFC marchand' : 'Merchant NFC tag'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
                            {isFr
                                ? 'Copiez cette reference sur un tag NFC MojoPayr pour ouvrir le paiement marchand. Ce n est pas un paiement carte sans contact certifie.'
                                : 'Copy this reference payload to a MojoPayr NFC tag to open merchant payment. This is not certified card-present tap-to-pay.'}
                        </Typography>
                        <Stack spacing={1.2}>
                            <Button
                                variant="outlined"
                                startIcon={<ContentCopyRoundedIcon />}
                                onClick={() => handleCopyPayload(qrValue)}
                                disabled={!qrValue}
                                sx={{
                                    color: '#FFFFFF',
                                    borderColor: 'rgba(183,166,255,0.34)',
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    fontWeight: 800,
                                }}
                            >
                                {isFr ? 'Copier payload NFC' : 'Copy NFC Payload'}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<NfcRoundedIcon />}
                                disabled={!qrValue}
                                onClick={() => handleCopyPayload(qrValue)}
                                sx={{
                                    bgcolor: '#FF8A00',
                                    color: '#0B0620',
                                    borderRadius: 4,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    '&:hover': { bgcolor: '#ff9c2a' },
                                }}
                            >
                                {isFr ? 'Copier payload NFC' : 'Copy NFC Payload'}
                            </Button>
                            {paymentRequest?.reference && (
                                <Button
                                    variant="outlined"
                                    startIcon={<ContentCopyRoundedIcon />}
                                    onClick={() => handleCopyPayload(paymentRequest.reference)}
                                    sx={{
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(183,166,255,0.34)',
                                        borderRadius: 4,
                                        textTransform: 'none',
                                        fontWeight: 800,
                                    }}
                                >
                                    {isFr ? 'Copier code paiement' : 'Copy Payment Code'}
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>
                            {isFr ? 'Demande de paiement' : 'Payment request'}
                        </Typography>
                        {paymentRequest ? (
                            <Stack spacing={1}>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13, wordBreak: 'break-all' }}>
                                    {paymentRequest.reference}
                                </Typography>
                                <Typography sx={{ fontWeight: 900 }}>
                                    {paymentRequest.currency || currency} {Number(paymentRequest.amount || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ color: '#FF8A00', fontWeight: 900 }}>
                                    {isFr ? 'Total' : 'Total'} {paymentRequest.currency || currency} {Number(paymentRequest.total || paymentRequest.amount || 0).toFixed(2)}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr ? 'Statut' : 'Status'}: {String(paymentRequest.status || 'pending').toUpperCase()}
                                </Typography>
                            </Stack>
                        ) : (
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr
                                    ? 'Creez une demande pour obtenir un QR, un code et un payload NFC.'
                                    : 'Create a request to get a QR, payment code, and NFC payload.'}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 1 }}>
                            {isFr ? 'Demandes recentes' : 'Recent requests'}
                        </Typography>
                        {recentRequests.length > 0 ? (
                            <Stack spacing={1}>
                                {recentRequests.slice(0, 5).map((request) => (
                                    <Box key={request.id || request.reference} sx={{ ...shellStyles.softPanel, p: 1.5 }}>
                                        <Typography sx={{ fontWeight: 800 }}>{request.reference}</Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                            {request.currency || currency} {Number(request.amount || 0).toFixed(2)} - {String(request.status || 'pending').toUpperCase()}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr ? 'Aucune demande recente.' : 'No recent requests yet.'}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            }
        />
    )
}
